import React, { ReactElement, ReactNode, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import InfoIcon from "@material-ui/icons/Info";
import WarningIcon from "@material-ui/icons/Warning";

import Text from "components/ui/Text";

import "./ToastProvider.scss";

export type ToastContextType = {
  showToast: ShowToastFunc;
  showLoadingToast: (message: string) => ToastHandle;
};

export type ShowToastFunc = (message: string, variant?: ToastVariant) => void;

/** Handle to a persistent loading toast; call success/error to resolve it in place. */
export type ToastHandle = {
  success: (message: string) => void;
  error: (message: string) => void;
  dismiss: () => void;
};

type ToastVariant = "success" | "error" | "warning" | "default";

const ToastContext = React.createContext<ToastContextType | null>(null);
ToastContext.displayName = "ToastContext";

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

type ProviderProps = {
  children: ReactNode;
};

export function ToastProvider({ children }: ProviderProps) {
  const showToast = useCallback((message: string, variant: ToastVariant = "default") => {
    toast(<Text variant="body-2">{message}</Text>, { icon: icons[variant] });
  }, []);

  const showLoadingToast = useCallback((message: string): ToastHandle => {
    const id = toast.loading(<Text variant="body-2">{message}</Text>);
    const resolve = (msg: string, variant: "success" | "error") =>
      toast.update(id, {
        render: <Text variant="body-2">{msg}</Text>,
        type: variant,
        icon: icons[variant],
        isLoading: false,
        autoClose: 5000,
      });
    return {
      success: msg => resolve(msg, "success"),
      error: msg => resolve(msg, "error"),
      dismiss: () => toast.dismiss(id),
    };
  }, []);

  const value = React.useMemo(
    () => ({ showToast, showLoadingToast }),
    [showToast, showLoadingToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
      />
    </ToastContext.Provider>
  );
}

const icons: Record<ToastVariant, ReactElement> = {
  success: <CheckCircleIcon fontSize="large" htmlColor="#008057" />, // --Success-6
  error: <ErrorIcon fontSize="large" htmlColor="#c42b46" />, // --Error-6 (was Material red 700)
  warning: <WarningIcon fontSize="large" htmlColor="#c74600" />, // --Warning-6 (was Material orange 500)
  default: <InfoIcon fontSize="large" htmlColor="#424a59" />, // --grey-7-text-normal
};
